import memberDetailsService from "@services/memberDetails.service";
import React, { useEffect, createContext, useContext, useState } from "react";
import { useVitalsContext } from "../useVitals";
import { Props as ApexChartProps } from 'react-apexcharts'
import { cloneDeep } from "lodash";
import { formatDisplayDate } from "@utils/helpers";
import { Vital as VitalType } from '../useVitals'

export type VitalChartOptions = {
  vital: VitalType,
  formatChartData?: (data: any[]) => any[],
  chartProps?: Partial<ApexChartProps> | ((chartProps: Partial<ApexChartProps>, unit: string) => Partial<ApexChartProps>)
}

export type UseChartProps = {
  close?: () => void,
  name?: string,
  vitalsOptions?: {
    [key: string]: VitalChartOptions
  }
};

export type UseChart = (props: UseChartProps) => {
  chartRef?: React.MutableRefObject<any>
  loading: boolean
  chartProps?: ApexChartProps,
  vitalOptions?: VitalChartOptions
};

const defaultChartProps: (series?: any[], unit?: string) => Partial<ApexChartProps> = (series = [], unit = '') => {
  return {
    type: 'line',
    height: '500px',
    width: '100%',
    options: {
      chart: {
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        },
        selection:  {
          enabled: false
        }
      },
      markers: {
        size: 0,
        hover: {
          size: 8,
          sizeOffset: 5
        }
      },
      grid: {
        show: true,
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 3
      },
      tooltip: {
        y: {
          formatter: (value: number, opts?: any) => {
            return `${value} ${unit}`
          }
        }
      }
    },
    series: series
  }
}

const defaultformatChartData = (vital: VitalType, data: any[] = []) => {
  return [
    {
      name: vital.displayName,
      data: data.map(
        (datum: any) => {
          datum.x = formatDisplayDate(datum.refreshDate, 'createDateFormat')
          datum.y = datum.value
          return datum
        }
      )
    }
  ]
}

export const useChart: UseChart = function ({
  close,
  name = '',
  vitalsOptions = {}
}) {
  const {
    patientID
  } = useVitalsContext()
  
  const chartRef = React.useRef<any>()
  const [loading, setloading] = useState<boolean>(true)
  const [chartProps, setchartProps] = useState<ApexChartProps>(defaultChartProps())

  const vitalOptions = vitalsOptions[name]

  const updateChartOptions = React.useCallback(
    (values: any[] = [], unit: string = '') => {
      const formattedValues = (vitalOptions?.formatChartData && vitalOptions.formatChartData(values)) || defaultformatChartData(vitalOptions?.vital || ({} as VitalType), values)

      let chartProps = defaultChartProps(formattedValues, unit)

      if (values.length === 0 && chartProps && chartProps.options) {
        chartProps.options.noData =  {
          text: 'No Data Found'
        }
      }

      if (typeof (vitalOptions.chartProps) === 'function') {
        chartProps = vitalOptions.chartProps(chartProps, unit)
      } else if (vitalOptions.chartProps) {
        chartProps = {
          ...chartProps,
          ...vitalOptions.chartProps
        }
      }

      chartRef?.current?.chart?.updateOptions(cloneDeep(chartProps.options) as ApexChartProps)
      setchartProps(cloneDeep(chartProps)) 

      setloading(false)
    },
    [vitalOptions]
  )

  useEffect(
    () => {
      console.log('useChart.useEffect1')
      setloading(true)
      if (name) {
        memberDetailsService.fetchVitalsGraph(patientID, name)
          .subscribe((response) => {
            const values = response?.data?.data?.values || []
            const unit = response?.data?.data?.unit
            updateChartOptions(values as any[], unit as string)
          }, err => {
            const values: any[] = []
            const unit: string = ''
            updateChartOptions(values, unit)
          })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, patientID]
  )
  
  return {
    chartRef,
    loading,
    chartProps,
    vitalOptions
  };
};

export const ChartContext = createContext<ReturnType<UseChart>>({
  loading: true
});

export const ChartProvider: React.FC<UseChartProps> = function ({ children, ...props }) {
  const chartValue = useChart(props);

  return <ChartContext.Provider value={chartValue}>{children}</ChartContext.Provider>;
};

export const useChartContext = () => useContext(ChartContext);
